import { FC } from 'react'
import { FormattedMessage } from 'react-intl'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import RadioInput from 'components/shared/Radio/input'
import { formatPrice } from 'helpers/helpers'
import { useResolutions } from 'store/resolutions'
import {
    RadioButtonWrapper,
    StyledSimpleRadioTileGroupItem,
} from './SimpleRadioTileGroupItem.styles'
import { SimpleRadioTileGroupItemProps } from './SimpleRadioTileGroupItem.types'
import { StyledCrossedOutPrice } from 'components/shared/RadioTileGroup/subcomponents/RadioTileGroupItem/RadioTileGroupItem.styles'

export const SimpleRadioTileGroupItem: FC<SimpleRadioTileGroupItemProps> = ({
    id,
    value,
    label,
    description,
    variant = 'normal',
    name,
    price,
    priceLabel,
    regularPrice,
    historyPrice,
    width = ['100%', 180],
    isChecked = false,
    isDisabled = false,
    isError = false,
    isRadioMarkVisible = true,
    onChange,
    'data-testid': testId,
}: SimpleRadioTileGroupItemProps) => {
    const { isTablet } = useResolutions()

    const handleClick = () => {
        if (!isDisabled && !isChecked) {
            onChange?.(value)
        }
    }

    const shouldDisplayOmnibus =
        regularPrice !== undefined &&
        regularPrice !== null &&
        historyPrice !== undefined &&
        historyPrice !== null

    const isBigVariant = variant === 'big'
    const isNarrowVariant = variant === 'narrow'

    return (
        <StyledSimpleRadioTileGroupItem
            justify={isRadioMarkVisible ? 'flex-start' : 'center'}
            width={width}
            isChecked={isChecked}
            isDisabled={isDisabled}
            isError={isError}
            isRadioMarkVisible={isRadioMarkVisible}
            isBigVariant={isBigVariant}
            onClick={handleClick}
            data-testid={testId ? `${testId}-select` : undefined}
        >
            <Box direction="row" align="center" gap={16} isFullWidth>
                <RadioButtonWrapper
                    isVisible={
                        isRadioMarkVisible && (!isTablet || isBigVariant)
                    }
                >
                    <RadioInput
                        id={id}
                        option={name || label}
                        value={value}
                        isChecked={isChecked}
                        isDisabled={isDisabled}
                        ariaLabelledBy={`${id}-label`}
                    />
                </RadioButtonWrapper>
                <Box
                    direction={
                        (isNarrowVariant && !isTablet) || !shouldDisplayOmnibus
                            ? 'column-reverse'
                            : 'row'
                    }
                    align="center"
                    justify={isNarrowVariant ? 'flex-start' : 'space-between'}
                    isFullWidth={!isNarrowVariant || isTablet}
                >
                    <Box
                        align={!isRadioMarkVisible ? 'center' : undefined}
                        isFullWidth
                    >
                        <Text
                            variant="body"
                            id={`${id}-label`}
                            color="grayscale_900"
                        >
                            {label}
                        </Text>
                        {!!description && (
                            <Text
                                variant={isBigVariant ? 'body' : 'bodySmall'}
                                color="grayscale_800"
                                isCapitalized
                            >
                                {description}
                            </Text>
                        )}
                    </Box>
                    {price !== undefined && (
                        <Box
                            align={
                                (isNarrowVariant && !isTablet) ||
                                !shouldDisplayOmnibus
                                    ? 'flex-start'
                                    : 'flex-end'
                            }
                            isFullWidth
                        >
                            {regularPrice !== undefined &&
                                regularPrice !== null && (
                                    <StyledCrossedOutPrice
                                        direction="row"
                                        gap={4}
                                        align="baseline"
                                    >
                                        <Text
                                            variant="bodySmall"
                                            color="grayscale_800"
                                        >
                                            {formatPrice(regularPrice)}
                                        </Text>
                                        <Text
                                            variant="bodyExtraSmall"
                                            color="grayscale_800"
                                            as="span"
                                        >
                                            PLN
                                        </Text>
                                    </StyledCrossedOutPrice>
                                )}
                            <Box
                                direction="row"
                                mt={[2, 0]}
                                gap={4}
                                align="baseline"
                                justify="flex-end"
                                isFullWidth={!isTablet}
                            >
                                <Text
                                    variant={
                                        isBigVariant ? 'heading2' : 'heading4'
                                    }
                                    color="secondary_500"
                                >
                                    {formatPrice(price)}
                                </Text>
                                <Text
                                    variant={
                                        isBigVariant
                                            ? 'bodyStrong'
                                            : 'bodyExtraSmallStrong'
                                    }
                                    color="secondary_500"
                                >
                                    PLN
                                </Text>
                            </Box>
                            {!!priceLabel && (
                                <Text
                                    variant="bodySmall"
                                    color="grayscale_800"
                                    hasNoWrap
                                >
                                    {priceLabel}
                                </Text>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
            {/* 'omnibus' legend, displays:
                - regular, minimum price
                - historical, minimum price of 30-day period
                ! is visible only if:
                - values are passed from api
            */}
            {shouldDisplayOmnibus && (
                <Box direction="column" gap={4} align="flex-end" isFullWidth>
                    <Text
                        variant="bodyExtraSmall"
                        color="grayscale_800"
                        align="right"
                    >
                        <FormattedMessage
                            id="global.discount.regular_price"
                            defaultMessage="_"
                            values={{
                                price: formatPrice(regularPrice),
                            }}
                        />
                    </Text>
                    <Text
                        variant="bodyExtraSmall"
                        color="grayscale_800"
                        align="right"
                    >
                        <FormattedMessage
                            id="global.discount.lowest_price_30_days"
                            defaultMessage="_"
                            values={{
                                price: formatPrice(historyPrice),
                            }}
                        />
                    </Text>
                </Box>
            )}
        </StyledSimpleRadioTileGroupItem>
    )
}
